import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Share2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  listJobShares,
  resendShareInvite,
  revokeJobShare,
  setShareDownload,
  shareJob,
} from "@/lib/sharing.functions";

/**
 * Owner-only: add a new share by email (with a per-share download toggle,
 * defaulting off) and manage existing ones — revoke, or flip download
 * access — for this one job. Sharing is single-hop (recipients can't
 * re-share) and per-job (no "always share with X" standing rule yet).
 *
 * Lives on the dashboard (My uploads), not the report page — the owner
 * shouldn't have to open a report just to share it.
 */
export function SharePanel({ jobId }: { jobId: string }) {
  const queryClient = useQueryClient();
  const shareFn = useServerFn(shareJob);
  const listFn = useServerFn(listJobShares);
  const revokeFn = useServerFn(revokeJobShare);
  const setDownloadFn = useServerFn(setShareDownload);
  const resendFn = useServerFn(resendShareInvite);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [canDownload, setCanDownload] = useState(false);

  const { data: shares } = useQuery({
    queryKey: ["job-shares", jobId],
    queryFn: () => listFn({ data: { jobId } }),
    enabled: open,
  });

  const shareMutation = useMutation({
    mutationFn: () => shareFn({ data: { jobId, email: email.trim(), canDownload } }),
    onSuccess: (result) => {
      setEmail("");
      setCanDownload(false);
      queryClient.invalidateQueries({ queryKey: ["job-shares", jobId] });
      toast.success("Invite sent — they'll get access once they accept it.");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't share this file."),
  });

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => revokeFn({ data: { shareId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["job-shares", jobId] }),
    onError: () => toast.error("Couldn't revoke that share just now."),
  });

  const toggleDownloadMutation = useMutation({
    mutationFn: (vars: { shareId: string; canDownload: boolean }) =>
      setDownloadFn({ data: vars }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["job-shares", jobId] }),
    onError: () => toast.error("Couldn't update that just now."),
  });

  const resendMutation = useMutation({
    mutationFn: (shareId: string) => resendFn({ data: { shareId } }),
    onSuccess: () => toast.success("Invite email sent again."),
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Couldn't resend that invite."),
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Share2 className="size-4" /> Share
      </Button>
    );
  }

  return (
    <div className="mt-2 w-full rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-base">
          <Share2 className="size-4 text-primary" /> Share this report
        </h3>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        They'll be able to browse and search this report like you can, but can't delete it, rename
        it, or share it onward. You can revoke access at any time.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) shareMutation.mutate();
        }}
        className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center"
      >
        <Input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="their@email.com"
          className="flex-1"
          required
        />
        <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
          <Checkbox checked={canDownload} onCheckedChange={(v) => setCanDownload(v === true)} />
          Can download
        </label>
        <Button type="submit" disabled={shareMutation.isPending} className="shrink-0">
          Share
        </Button>
      </form>

      {shares && shares.length > 0 && (
        <ul className="mt-5 divide-y divide-border border-t border-border">
          {shares.map((share) => (
            <li key={share.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="text-sm font-medium">{share.invited_email}</p>
                <p className="text-xs text-muted-foreground">
                  {share.status === "pending" && "Invited — not yet accepted"}
                  {share.status === "active" && "Has access"}
                  {share.status === "declined" && "Declined the invite"}
                </p>
              </div>
              {share.status === "declined" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => revokeMutation.mutate(share.id)}
                >
                  Remove
                </Button>
              ) : (
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={share.can_download}
                      onCheckedChange={(checked) =>
                        toggleDownloadMutation.mutate({ shareId: share.id, canDownload: checked })
                      }
                    />
                    Can download
                  </label>
                  {share.status === "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={resendMutation.isPending}
                      onClick={() => resendMutation.mutate(share.id)}
                    >
                      Resend invite
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => revokeMutation.mutate(share.id)}
                  >
                    Revoke
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
